import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectCrossClientSelectedTab,
  selectCrossClientTabCounts,
  setCrossClientSelectedTab,
  type CrossClientTabCounts,
} from '../../../../store/crossClient/crossClientResults'
import { CROSS_CLIENT_RESULT_TAB_ORDER, CrossClientResultTabs as ResultTabs, type CrossClientResultTab } from '../../../../models/CrossClient'
import { ReactComponent as MatchIcon } from '../../../../assets/images/cross-client-match.svg'
import { ReactComponent as PotentialMatchIcon } from '../../../../assets/images/cross-client-potential-match.svg'
import { ReactComponent as NoMatchIcon } from '../../../../assets/images/cross-client-no-match.svg'
import './CrossClientResultTabs.scss'

const TAB_SLUGS: Record<CrossClientResultTab, string> = {
  [ResultTabs.MATCH]: 'match',
  [ResultTabs.POTENTIAL]: 'potential',
  [ResultTabs.REMAINING]: 'remaining',
  [ResultTabs.NO_MATCH]: 'noMatch',
  [ResultTabs.MISSING_DATA]: 'missingData',
}

const SLUG_TO_TAB: Record<string, CrossClientResultTab> = Object.fromEntries(
  Object.entries(TAB_SLUGS).map(([tab, slug]) => [slug, tab as CrossClientResultTab]),
)

interface TabConfig {
  tab: CrossClientResultTab
  label: string
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>
}

const TAB_CONFIG: Record<CrossClientResultTab, TabConfig> = {
  [ResultTabs.MATCH]: { tab: ResultTabs.MATCH, label: 'Match', Icon: MatchIcon },
  [ResultTabs.POTENTIAL]: { tab: ResultTabs.POTENTIAL, label: 'Potential', Icon: PotentialMatchIcon },
  [ResultTabs.REMAINING]: { tab: ResultTabs.REMAINING, label: 'Remaining' },
  [ResultTabs.NO_MATCH]: { tab: ResultTabs.NO_MATCH, label: 'No match', Icon: NoMatchIcon },
  [ResultTabs.MISSING_DATA]: { tab: ResultTabs.MISSING_DATA, label: 'Missing data' },
}

const COUNT_KEY_BY_TAB: Record<CrossClientResultTab, keyof CrossClientTabCounts> = {
  [ResultTabs.MATCH]: 'match',
  [ResultTabs.POTENTIAL]: 'potential',
  [ResultTabs.REMAINING]: 'remaining',
  [ResultTabs.NO_MATCH]: 'noMatch',
  [ResultTabs.MISSING_DATA]: 'missingData',
}

interface IndicatorStyle {
  left: number
  top: number
  width: number
  height: number
}

export const CrossClientResultTabs = () => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const counts = useAppSelector(selectCrossClientTabCounts)

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicator, setIndicator] = useState<IndicatorStyle | null>(null)

  const initialized = useRef(false)
  useEffect(() => {
    if (initialized.current) {
      return
    }
    initialized.current = true
    const tabParam = searchParams.get('tab')
    const tab = tabParam ? SLUG_TO_TAB[tabParam] : undefined
    if (tab && tab !== selectedTab) {
      dispatch(setCrossClientSelectedTab(tab))
    }
  }, [dispatch, searchParams, selectedTab])

  const handleSelect = (tab: CrossClientResultTab) => {
    if (tab === selectedTab) {
      return
    }
    dispatch(setCrossClientSelectedTab(tab))
    setSearchParams(
      (prev) => {
        prev.set('tab', TAB_SLUGS[tab])
        prev.delete('page')
        return prev
      },
      { replace: true },
    )
  }

  const visibleTabs = CROSS_CLIENT_RESULT_TAB_ORDER.filter((tab) => tab !== ResultTabs.MISSING_DATA || counts.missingData > 0)

  const activeIndex = visibleTabs.indexOf(selectedTab)
  const countsKey = visibleTabs.map((tab) => counts[COUNT_KEY_BY_TAB[tab]]).join(',')
  useLayoutEffect(() => {
    const measure = () => {
      const el = buttonRefs.current[activeIndex]
      if (el) {
        setIndicator({ left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight })
      }
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [activeIndex, countsKey])

  return (
    <div className='position-relative d-flex align-items-center gap-1 cross-client-result-tabs'>
      {indicator ? (
        <div
          className='cross-client-result-tabs__indicator'
          style={{ transform: `translateX(${indicator.left}px)`, top: indicator.top, width: indicator.width, height: indicator.height }}
        />
      ) : null}
      {visibleTabs.map((tab, index) => {
        const { label, Icon } = TAB_CONFIG[tab]
        return (
          <React.Fragment key={tab}>
            {tab === ResultTabs.NO_MATCH ? <span className='cross-client-result-tabs__separator' aria-hidden='true' /> : null}
            <button
              ref={(el) => {
                buttonRefs.current[index] = el
              }}
              type='button'
              aria-pressed={tab === selectedTab}
              className={classNames('d-flex align-items-center gap-1 cross-client-result-tabs__tab', {
                'cross-client-result-tabs__tab--active': tab === selectedTab,
              })}
              onClick={() => handleSelect(tab)}
            >
              {Icon ? <Icon width={12} height={12} /> : <span className='cross-client-result-tabs__incomplete-icon' aria-hidden='true' />}
              <span>
                {label} {counts[COUNT_KEY_BY_TAB[tab]].toLocaleString()}
              </span>
            </button>
          </React.Fragment>
        )
      })}
    </div>
  )
}
